import { useRef, useState } from 'react';
import { Camera, ImagePlus } from 'lucide-react';
import { api, ApiError } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { CameraScreen } from './CameraScreen.tsx';
import './ChatAvatarPicker.css';

/**
 * The two ways of putting a picture on a chat that wears one of its own: the
 * file that is already there, and the shot that has not been taken yet.
 *
 * Both ways rather than one, as «Профиль» offers them, because the answer
 * differs by device: a phone in somebody's hand wants the camera, and
 * everywhere else the picture already exists.
 *
 * Not optimistic, unlike the avatar on one's own profile: there is no tile
 * already on screen holding these bytes to move a badge onto, and the file has
 * to be re-encoded on the server before anything can draw it — so the tile
 * changes when the id comes back.
 *
 * The camera is rendered from here rather than returned by the screen that
 * hosts this. `.camera-screen` is fixed and over everything, so it covers the
 * page from inside it just as well as it does in place of it, and the two
 * screens that offer a picture no longer each need their own copy of the state
 * that says whether it is open.
 */
export function ChatAvatarPicker({
  chatId,
  onChanged,
}: {
  chatId: string;
  /** The id that came back, for the tile above to start wearing. */
  onChanged: (avatarPhotoId: string) => void;
}) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function change(file: File) {
    setUploading(true);
    setError(null);
    try {
      const { avatarPhotoId } = await api.setChatAvatar(chatId, file);
      onChanged(avatarPhotoId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сменить аватар');
    }
    setUploading(false);
  }

  return (
    <>
      <div className="chat-avatar-actions">
        <button
          type="button"
          className="chat-avatar-add"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
        >
          <ImagePlus size={17} />
          Из галереи
        </button>
        <button
          type="button"
          className="chat-avatar-add"
          onClick={() => setCameraOpen(true)}
          disabled={uploading}
        >
          <Camera size={17} />
          Снять фото
        </button>
      </div>
      {error && <div className="form-error">{error}</div>}

      <input
        ref={fileInput}
        className="chat-avatar-file"
        type="file"
        accept="image/*"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file twice still fires `change`.
          event.target.value = '';
          if (file) void change(file);
        }}
      />

      {cameraOpen && (
        <CameraScreen
          maxUploadBytes={user?.maxUploadBytes ?? 0}
          photoOnly
          onCapture={(file) => {
            setCameraOpen(false);
            void change(file);
          }}
          onGallery={() => {
            setCameraOpen(false);
            fileInput.current?.click();
          }}
          onSystemCamera={null}
          onClose={() => setCameraOpen(false)}
        />
      )}
    </>
  );
}
